/**
 * useCreateManagedSqlDb Hook
 *
 * Custom React Query mutation hook for creating managed SQL databases.
 */

import { useMutation, useQueryClient } from '@tanstack/react-query';
import { managedSqlDbService } from '../../../api/managed-sql-db.service';
import type { ManagedSqlDbCreateDTO } from '../../../types/managed-sql-db.types';
import { getErrorMessage } from '../../../lib/utils/errors';

interface UseCreateManagedSqlDbOptions {
  onSuccess?: () => void;
  onError?: (error: string) => void;
}

/**
 * Hook for creating managed SQL database
 * @param tenantId - Tenant ID
 * @param options - Success/error callbacks
 */
export const useCreateManagedSqlDb = (tenantId: string, options?: UseCreateManagedSqlDbOptions) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: ManagedSqlDbCreateDTO) =>
      managedSqlDbService.createManagedSqlDb(tenantId, data),
    onSuccess: () => {
      // Invalidate all managed SQL database queries for this tenant
      queryClient.invalidateQueries({
        queryKey: ['managedSqlDbs', tenantId],
        exact: false,
      });
      options?.onSuccess?.();
    },
    onError: (error: unknown) => {
      const errorMessage = getErrorMessage(error);
      options?.onError?.(errorMessage);
    },
  });
};
